import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import parseJSON from 'date-fns/parseJSON'
import { getStoredToken } from '../../../lib/auth'
import { url } from '../../../lib/api'
import { useFetch } from '../../../hooks'
import Table, {
  Body,
  Column,
  Head,
  Header,
  Row,
} from '../../../components/Table'
import ValveFailureIcon from '../../map/ValveFailureIcon'
import { kindTranslationKey, periodsOfValve, splitDuration } from './utils'

// History of the valve input mismatches (valve open without pressure / closed with pressure) of the last 30 days
const InputMismatchHistory = ({ valveId, inputMismatch }) => {
  const { t } = useTranslation()
  const token = getStoredToken()
  const options = useMemo(
    () => ({
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  )
  // reload when the current input mismatch changes (live update of the valve)
  const { data, error } = useFetch({
    url: `${url}/valves/${valveId}/input_mismatch_periods`,
    options,
    refetch: inputMismatch,
    skip: !valveId,
  })

  if (error) {
    return <p>{t('attributes:valve.inputMismatchHistory.loadingFailed')}</p>
  }
  if (!data) {
    return <p>{t('attributes:valve.inputMismatchHistory.loading')}</p>
  }

  const periods = periodsOfValve(data, valveId)
  if (periods.length === 0) {
    return <p>{t('attributes:valve.inputMismatchHistory.empty')}</p>
  }

  return (
    <Table divided>
      <Head>
        <Row>
          <Header>{t('attributes:valve.inputMismatchHistory.kind')}</Header>
          <Header>
            {t('attributes:valve.inputMismatchHistory.startedAt')}
          </Header>
          <Header>{t('attributes:valve.inputMismatchHistory.endedAt')}</Header>
          <Header>{t('attributes:valve.inputMismatchHistory.duration')}</Header>
        </Row>
      </Head>
      <Body>
        {periods.map(
          ({ id, kind, startedAt, endedAt, durationSeconds, ongoing }) => (
            <Row key={id}>
              <Column>
                <ValveFailureIcon inputMismatch={kind} />{' '}
                {t(kindTranslationKey(kind))}
              </Column>
              <Column>
                {t('format:dateTimeShort', { date: parseJSON(startedAt) })}
              </Column>
              <Column>
                {ongoing
                  ? t('attributes:valve.inputMismatchHistory.ongoing')
                  : t('format:dateTimeShort', { date: parseJSON(endedAt) })}
              </Column>
              <Column>
                {t(
                  'attributes:valve.inputMismatchHistory.durationValue',
                  splitDuration(durationSeconds)
                )}
              </Column>
            </Row>
          )
        )}
      </Body>
    </Table>
  )
}

export default InputMismatchHistory
